import type { ArticleAttributeType } from '../Types/ArticleAttribute';
import { ArticleAttribute } from '../Types/ArticleAttribute';
import { ArticleAttributeSlot } from '../Types/ArticleAttributeSlot';
import {
  ARTICLE_ATTRIBUTE_IMAGE_RENDITIONS,
  generateAssetRenditionUrl,
  ImageFormat,
} from '../Utils/Assets';
import { getApiInstance } from './Api';

export const mapArticleAttribute = (
  articleAttribute: any,
): ArticleAttribute => {
  // TODO this should be addressed later on so system items (currently delivered from shared S3 bucket) also come from cloudfront and we store relative paths for them too
  const imageUrl =
    articleAttribute.ownerId && articleAttribute.imageUrl
      ? new URL(
          articleAttribute.imageUrl,
          window.env.REACT_APP_MEDIA_BASE_URL,
        ).toString()
      : articleAttribute.imageUrl;

  return {
    id: articleAttribute.id,
    type: articleAttribute.type,
    title: articleAttribute.title,
    description: articleAttribute.description,
    image: imageUrl && {
      originalUrl: imageUrl,
      compressedUrl: generateAssetRenditionUrl(
        imageUrl,
        null,
        ImageFormat.WEBP,
      ),
      thumbnailUrl: generateAssetRenditionUrl(
        imageUrl,
        ARTICLE_ATTRIBUTE_IMAGE_RENDITIONS.THUMBNAIL,
        ImageFormat.WEBP,
      ),
    },
    ownerId: articleAttribute.ownerId,
  };
};

const mapArticleAttributeSlot = (
  articleAttributeSlot: ArticleAttributeSlot,
): ArticleAttributeSlot => {
  return {
    name: articleAttributeSlot.name,
    title: articleAttributeSlot.title,
    description: articleAttributeSlot.description,
    attributeType: articleAttributeSlot.attributeType,
    maxItems: articleAttributeSlot.maxItems,
    isRequired: articleAttributeSlot.isRequired,
  };
};

export const saveAttribute = async (attributeData: {
  name: string;
  description: string;
  type: ArticleAttributeType;
  attributeId?: string;
  imageFileName?: string;
}): Promise<ArticleAttribute> => {
  console.log(attributeData);
  const response = await getApiInstance()
    .post('/article-attributes/attributes', attributeData)
    .catch((error) => {
      throw new Error(
        error.response?.data?.error ??
          (attributeData.attributeId
            ? `Unknown error updating your attribute`
            : `Unknown error creating your attribute`),
      );
    });

  return mapArticleAttribute(response.data);
};

export const deleteAttribute = async (attributeId: string): Promise<void> => {
  await getApiInstance()
    .delete(`/article-attributes/attributes/${attributeId}`)
    .catch((error) => {
      throw new Error(
        error.response?.data?.error ?? 'Unknown error deleting this attribute',
      );
    });
};

export const fetchArticleAttributeSlots = async (): Promise<
  ArticleAttributeSlot[]
> => {
  const response = await getApiInstance().get('/article-attributes/slots');
  return response.data.map((articleAttributeSlot: ArticleAttributeSlot) =>
    mapArticleAttributeSlot(articleAttributeSlot),
  );
};

export const fetchArticleAttributes = async (): Promise<ArticleAttribute[]> => {
  try {
    const response = await getApiInstance().get('/article-attributes');
    return response.data.map((articleAttribute: ArticleAttribute) =>
      mapArticleAttribute(articleAttribute),
    );
  } catch (error) {
    console.error('Error fetching article attributes', error);
    throw error;
  }
};

export const uploadAttributeImage = async (
  file: File,
  type: ArticleAttributeType,
): Promise<{ url: string; imageFileName: string }> => {
  try {
    const formData = new FormData();
    formData.append('image', file);

    const response = await getApiInstance().put(
      `/article-attributes/attributes/upload-image/${type}`,
      formData,
      {
        headers: {
          'Content-Type': 'multipart/form-data',
        },
      },
    );

    return response.data;
  } catch (error) {
    console.error('Error uploading character image', error);
    throw new Error(
      error.response?.data?.error ?? 'Unknown error uploading the image',
    );
  }
};

export const fetchArticleAttributeImageGenerationStatus = async (
  articleAttributeId: string,
  originalImageUrl: string | null,
): Promise<{ status: string; articleAttribute?: ArticleAttribute }> => {
  const baseUrl = `/article-attributes/generate/image/status?articleAttributeId=${articleAttributeId}`;
  try {
    const response = await getApiInstance().get(
      originalImageUrl
        ? `${baseUrl}&originalImageUrl=${originalImageUrl}`
        : baseUrl,
    );
    return {
      status: response.data.status,
      articleAttribute: response.data.articleAttribute
        ? mapArticleAttribute(response.data.articleAttribute)
        : null,
    };
  } catch (error) {
    console.error(
      'Error fetching article attribute image generation status',
      error,
    );
    throw error;
  }
};

export const fetchArticleAttribute = async (
  attributeId: string,
): Promise<ArticleAttribute> => {
  try {
    const response = await getApiInstance().get(
      `/article-attributes/attributes/${attributeId}`,
    );
    return mapArticleAttribute(response.data);
  } catch (error) {
    console.error('Error fetching article attribute', error);
    throw error;
  }
};
